import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { brand } from "../lib/brand";
import {
  getAllCoursesForAdmin,
  getEnrolledCourses,
  getLessons,
  getProgressForLessons,
  reorderCourses,
  updateCourse,
  type Course,
} from "../lib/firebase";
import { moveItem, reorderWrites } from "../lib/lessonOrdering";
import { groupCourses, placeBefore } from "../lib/courseGroups";
import { AMF_COURSE_IDS } from "../lib/homework";

const AdminCourseEditPage = lazy(() => import("./admin/AdminCourseEditPage").then((m) => ({ default: m.AdminCourseEditPage })));

// Skool-style card progress: % of published lessons completed.
type CardProgress = { percent: number; completed: number; total: number };

export function DashboardPage() {
  const { user, isAdmin, previewTier } = useAuth();
  // Admin edits in place, Skool-style: ?edit=<courseId> or ?edit=new opens the course settings here.
  const [params, setParams] = useSearchParams();
  const editId = isAdmin ? params.get("edit") : null;
  // ?group=<name> opens one group card to show the courses inside it.
  const groupName = params.get("group");
  const [reloadKey, setReloadKey] = useState(0);
  const [courses, setCourses] = useState<Course[] | null>(null);
  const [progress, setProgress] = useState<Map<string, CardProgress>>(new Map());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // Admin sees every course, drafts included, so they can edit from here.
        // Previewing a tier: what a buyer of that tier gets (published courses tagged with it).
        const list = isAdmin
          ? await getAllCoursesForAdmin()
          : previewTier
            ? (await getAllCoursesForAdmin()).filter((c) => c.published && c.tiers.includes(previewTier))
            : await getEnrolledCourses(user.uid);
        setCourses(list); // paint cards first, progress bars fill in after
        const entries = await Promise.all(
          list.map(async (c) => {
            const lessons = (await getLessons(c.id)).filter((l) => l.published);
            const prog = await getProgressForLessons(user.uid, lessons.map((l) => l.id));
            const completed = lessons.filter((l) => prog.get(l.id)?.completedAt).length;
            const percent = lessons.length === 0 ? 0 : Math.round((completed / lessons.length) * 100);
            return [c.id, { percent, completed, total: lessons.length }] as const;
          })
        );
        setProgress(new Map(entries));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [user, isAdmin, previewTier, reloadKey]);

  // Admin drag & drop (Dew, 2026-10-20): drop on a card's left edge = move in front of it,
  // drop on the rest of a card = put them in one group. Inside a group view every drop just reorders.
  const [drag, setDrag] = useState<{ ids: string[]; group?: string } | null>(null);
  const [over, setOver] = useState<{ key: string; mode: "before" | "group" } | null>(null);

  async function saveOrder(next: Course[]) {
    setCourses(next.map((c, i) => ({ ...c, order: i + 1 })));
    await reorderCourses(reorderWrites(next.map((c) => ({ id: c.id, order: c.order ?? 0 }))));
  }

  async function setGroup(ids: string[], group: string) {
    await Promise.all(ids.map((id) => updateCourse(id, { group })));
    setCourses((cs) => cs && cs.map((c) => (ids.includes(c.id) ? { ...c, group } : c)));
  }

  const dropMode = (e: React.DragEvent<HTMLLIElement>): "before" | "group" => {
    const box = e.currentTarget.getBoundingClientRect();
    return groupName || e.clientX - box.left < box.width * 0.3 ? "before" : "group";
  };

  function dropProps(key: string, ids: string[], group?: string) {
    if (!isAdmin) return {};
    return {
      draggable: true,
      onDragStart: () => setDrag({ ids, group }),
      onDragEnd: () => { setDrag(null); setOver(null); },
      onDragOver: (e: React.DragEvent<HTMLLIElement>) => {
        if (!drag || drag.ids.includes(ids[0])) return;
        e.preventDefault();
        const mode = dropMode(e);
        if (over?.key !== key || over.mode !== mode) setOver({ key, mode });
      },
      onDragLeave: () => setOver((o) => (o?.key === key ? null : o)),
      onDrop: async (e: React.DragEvent<HTMLLIElement>) => {
        e.preventDefault();
        const d = drag, mode = dropMode(e); // decided where the card lands, not by the last hover
        setDrag(null);
        setOver(null);
        if (!d || !courses || d.ids.includes(ids[0])) return;
        try {
          if (mode === "before") return await saveOrder(placeBefore(courses, d.ids, ids[0]));
          // Grouping: join the target's group, else the dragged group, else name a new one.
          const name = group || d.group || window.prompt("ตั้งชื่อกลุ่มใหม่", "กลุ่มใหม่")?.trim();
          if (!name) return;
          await setGroup([...d.ids, ...ids], name);
          await saveOrder(placeBefore(courses, d.ids, ids[0]));
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        }
      },
    };
  }
  const dropClass = (key: string) =>
    over?.key === key ? (over.mode === "before" ? " is-drop-before" : " is-drop-group") : "";

  async function renameGroup(from: string) {
    const to = window.prompt("ชื่อกลุ่มใหม่", from)?.trim();
    if (!to || to === from || !courses) return;
    await setGroup(courses.filter((c) => c.group?.trim() === from).map((c) => c.id), to);
    setParams({ group: to });
  }

  async function move(from: number, to: number) {
    if (!courses || to < 0 || to >= courses.length) return;
    const next = moveItem(courses, from, to);
    setCourses(next.map((c, i) => ({ ...c, order: i + 1 })));
    try {
      await reorderCourses(reorderWrites(next.map((c) => ({ id: c.id, order: c.order ?? 0 }))));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (editId) {
    return (
      <Suspense fallback={<p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>}>
      <AdminCourseEditPage
        key={editId}
        courseId={editId}
        onDone={() => {
          setParams({});
          setReloadKey((k) => k + 1);
        }}
      />
      </Suspense>
    );
  }

  if (error) return <main><p className="error">{error}</p></main>;
  if (courses === null) return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>;

  // Zero-course state - they're logged in but have no enrollment yet.
  if (courses.length === 0 && !isAdmin) {
    return (
      <main>
        <section className="hero-mint">
          <span className="kicker">Dashboard</span>
          <h1>ยังไม่มีคอร์ส</h1>
          <p style={{ color: "var(--c-text-muted)", marginTop: "0.75rem" }}>
            บัญชีที่ล็อกอินอยู่ตอนนี้คือ <strong>{user?.email}</strong>
          </p>
        </section>
        <div className="empty-state">
          <h2>ยังไม่พบคอร์สที่ลงทะเบียน</h2>
          {/* สาเหตุอันดับหนึ่งของหน้านี้คือเข้าคนละอีเมลกับตอนจ่ายเงิน บอกไปตรงๆ เลยจะได้ไม่ต้องทักมาถาม */}
          <p>
            สาเหตุที่เจอบ่อยที่สุด: ล็อกอินคนละอีเมลกับที่ใช้ตอนจ่ายเงิน
            <br />
            ลองออกจากระบบ แล้วเข้าใหม่ด้วยอีเมลที่ใช้ซื้อคอร์ส
          </p>
          <p>
            ถ้าเพิ่งซื้อ รอ 1-2 นาทีให้ระบบประมวลผล แล้ว refresh หน้านี้
            <br />
            {brand.id === "aisthetics"
              ? "ติดปัญหา ตอบกลับอีเมลยืนยันการสั่งซื้อได้เลย"
              : "ติดปัญหา ทักไปที่ Facebook Page ได้เลย"}
          </p>
        </div>
      </main>
    );
  }

  const items = groupName
    ? courses.filter((c) => c.group?.trim() === groupName).map((course) => ({ kind: "course" as const, course }))
    : groupCourses(courses);

  return (
    <main>
      <section className="hero-mint">
        {groupName ? (
          <button type="button" className="link-btn page-header__back" onClick={() => setParams({})}>← คอร์สของฉัน</button>
        ) : (
          <span className="kicker">Dashboard</span>
        )}
        <h1>{groupName ?? "คอร์สของฉัน"}</h1>
        <p style={{ color: "var(--c-text-muted)", marginTop: "0.75rem" }}>
          กลับมาเรียนต่อได้ทุกเมื่อ กดที่คอร์สเพื่อเริ่ม
        </p>
        {isAdmin && (
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1rem" }}>
            <button className="btn-primary" onClick={() => setParams({ edit: "new" })}>
              + คอร์สใหม่
            </button>
            {groupName && (
              <button className="btn-secondary" onClick={() => renameGroup(groupName)}>✏️ เปลี่ยนชื่อกลุ่ม</button>
            )}
          </div>
        )}
        {isAdmin && (
          <p className="dash-dnd-hint">
            {groupName
              ? "ลากการ์ดไปวางบนการ์ดอื่นเพื่อสลับลำดับ"
              : "ลากการ์ด: วางที่ขอบซ้ายของการ์ดอื่น = ย้ายไปไว้ข้างหน้า · วางกลางการ์ด = รวมเป็นกลุ่ม"}
          </p>
        )}
      </section>
      <ul className="course-grid">
        {items.map((item) => {
          if (item.kind === "group") {
            // One big card for the whole group; progress = all its lessons together.
            const sums = item.courses.reduce(
              (s, c) => ({ done: s.done + (progress.get(c.id)?.completed ?? 0), total: s.total + (progress.get(c.id)?.total ?? 0) }),
              { done: 0, total: 0 }
            );
            const pct = sums.total ? Math.round((sums.done / sums.total) * 100) : 0;
            const cover = item.courses.find((c) => c.thumbnailUrl)?.thumbnailUrl;
            return (
              <li key={`group-${item.name}`} className={`course-card course-card--group${dropClass(`group-${item.name}`)}`}
                {...dropProps(`group-${item.name}`, item.courses.map((c) => c.id), item.name)}>
                <Link to={`/dashboard?group=${encodeURIComponent(item.name)}`}>
                  {cover ? <img src={cover} alt="" loading="lazy" /> : <div className="course-card__placeholder">{item.name.charAt(0).toUpperCase()}</div>}
                  <h2>{item.name}</h2>
                  <div className="course-card__progress">
                    {sums.total > 0 && (
                      <div className="course-progress-bar">
                        <div className="course-progress-bar__fill" style={{ width: `${pct}%` }} />
                      </div>
                    )}
                    <span>{item.courses.length} คอร์สย่อย{sums.total > 0 ? ` · ${pct}%` : ""}</span>
                  </div>
                  <div className="course-card__meta">เปิดดูคอร์สในกลุ่ม →</div>
                </Link>
              </li>
            );
          }
          const c = item.course;
          const i = courses.indexOf(c);
          const p = progress.get(c.id);
          return (
            <li key={c.id} className={`course-card${c.published ? "" : " course-card--draft"}${dropClass(c.id)}`} {...dropProps(c.id, [c.id], c.group?.trim() || undefined)}>
              {isAdmin && (
                <div className="course-card__admin">
                  {/* Who sees this course, so admin knows at a glance (tiers = buyer groups). Drafts read like Skool's. */}
                  <span
                    className={`course-card__draft${c.published ? "" : " course-card__draft--off"}`}
                    title={c.published ? c.tiers.join(", ") || undefined : "ฉบับร่าง เห็นแค่คุณ"}
                  >
                    {!c.published ? "Draft" : c.tiers.length ? `เห็น: ${c.tiers.join(", ")}` : "ให้สิทธิ์ทีละคน"}
                  </span>
                  <button type="button" aria-label="เลื่อนไปก่อน" disabled={i === 0} onClick={() => move(i, i - 1)}>◀</button>
                  <button type="button" aria-label="เลื่อนไปหลัง" disabled={i === courses.length - 1} onClick={() => move(i, i + 1)}>▶</button>
                  <button type="button" onClick={() => setParams({ edit: c.id })}>✏️ แก้ไข</button>
                  {groupName && (
                    <button type="button" onClick={() => setGroup([c.id], "").catch((e) => setError(String(e)))}>ออกจากกลุ่ม</button>
                  )}
                </div>
              )}
              <Link to={`/courses/${c.id}`}>
                {c.thumbnailUrl
                  ? <img src={c.thumbnailUrl} alt="" loading="lazy" />
                  : <div className="course-card__placeholder">{(c.title || "·").charAt(0).toUpperCase()}</div>}
                <h2>{c.title}</h2>
                {p && p.total > 0 && (
                  <div className="course-card__progress">
                    <div className="course-progress-bar">
                      <div className="course-progress-bar__fill" style={{ width: `${p.percent}%` }} />
                    </div>
                    <span>{p.percent}% · เรียนแล้ว {p.completed}/{p.total} บท</span>
                  </div>
                )}
                <div className="course-card__meta">
                  {p && p.completed > 0 ? (p.percent >= 80 ? "เรียนจบแล้ว ✓ ทบทวนได้เลย" : "เรียนต่อ →") : "เริ่มเรียน →"}
                </div>
              </Link>
            </li>
          );
        })}
        {groupName === "AI Money Factory" && courses.some((c) => AMF_COURSE_IDS.includes(c.id)) && (
          <>
          <li className="course-card course-card--homework">
            <Link to="/journey">
              <div className="course-card__placeholder">🗺️</div>
              <h2>แผนที่ + ใบเซอร์ · AI Money Factory</h2>
              <div className="course-card__progress"><span>เดินให้ครบ 7 ด่าน แล้วรับใบประกาศนียบัตร</span></div>
              <div className="course-card__meta">ดูว่าคุณอยู่ด่านไหน →</div>
            </Link>
          </li>
          <li className="course-card course-card--homework">
            <Link to="/homework">
              <div className="course-card__placeholder">✍️</div>
              <h2>การบ้าน + Quiz · AI Money Factory</h2>
              <div className="course-card__progress"><span>รวมการบ้านและ Quiz ทุก Module ไว้ที่เดียว</span></div>
              <div className="course-card__meta">ทำ Quiz / ส่งการบ้าน ได้ชิ้นละ 25 แต้ม →</div>
            </Link>
          </li>
          </>
        )}
      </ul>
    </main>
  );
}
